import React, { useState, useEffect } from 'react';
import { getTasks } from '../api/tasks';
import { getProjects } from '../api/projects';
import { getAllUsers } from '../api/users';
import { getTimeEntries } from '../api/time-entries';
import { getWeeklyPlans } from '../api/weekly-plans';
import { getProgrammes } from '../api/programmes';
import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { TimeEntry } from '../types/time-entry';
import { WeeklyPlan } from '../types/weekly-plan';
import { Programme } from '../types/programme';
import {
    calculateProjectMetrics,
    calculateTaskMetrics,
    calculateResourceMetrics,
    prepareGanttData,
    calculateTimeTrackingMetrics,
    calculateUserUtilization,
    calculatePlanVsActuals,
    exportTimeEntriesToCSV,
} from '../utils/reportingUtils';
import GanttChart from '../components/GanttChart';
import OverdueTaskModal from '../components/OverdueTaskModal';
import TabNavigation, { Tab } from '../components/TabNavigation';
import Select from '../components/Select';
import Button from '../components/Button';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { showToast } from '../utils/toast';
import { BarChart3, Users, AlertTriangle, Clock, TrendingUp, List } from 'lucide-react';

// Tab components
import OverviewTab from '../components/reports/OverviewTab';
import ResourcesTab from '../components/reports/ResourcesTab';
import IssuesTab from '../components/reports/IssuesTab';
import TimeTrackingTab from '../components/reports/TimeTrackingTab';
import PlanVsActualTab from '../components/reports/PlanVsActualTab';

const ReportsPage: React.FC = () => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
    const [weeklyPlans, setWeeklyPlans] = useState<WeeklyPlan[]>([]);
    const [programmes, setProgrammes] = useState<Programme[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'resources' | 'gantt' | 'issues' | 'time-tracking' | 'plan-vs-actual'>('overview');
    const [rescheduleTask, setRescheduleTask] = useState<Task | null>(null);
    
    // Filter states
    const [selectedProgramme, setSelectedProgramme] = useState<string>('');
    const [selectedProject, setSelectedProject] = useState<string>('');
    
    // Date range for time tracking reports (default: last 30 days)
    const [dateRangeStart, setDateRangeStart] = useState<string>(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    });
    const [dateRangeEnd, setDateRangeEnd] = useState<string>(() => {
        return new Date().toISOString().split('T')[0];
    });

    // Week selection for plan vs actual
    const [selectedWeekStart, setSelectedWeekStart] = useState<string>(() => {
        const today = new Date();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);
        return monday.toISOString().split('T')[0];
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                const [tasksData, projectsData, usersData, timeEntriesData, weeklyPlansData, programmesData] = await Promise.all([
                    getTasks(),
                    getProjects(),
                    getAllUsers(),
                    getTimeEntries(),
                    getWeeklyPlans(),
                    getProgrammes(),
                ]);
                setTasks(tasksData);
                setProjects(projectsData);
                setUsers(usersData);
                setTimeEntries(timeEntriesData);
                setWeeklyPlans(weeklyPlansData);
                setProgrammes(programmesData);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const handleRescheduleSuccess = (updatedTask: Task) => {
        setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
        setRescheduleTask(null);
    };

    const handleExportCSV = () => {
        const filteredEntries = timeEntries.filter(entry => 
            entry.date >= dateRangeStart && entry.date <= dateRangeEnd
        );
        const csvContent = exportTimeEntriesToCSV(filteredEntries, projects, users, tasks);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `time-entries-${dateRangeStart}-to-${dateRangeEnd}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast.success('Time entries exported successfully!');
    };

    const getWeekEndDate = (weekStart: string): string => {
        const start = new Date(weekStart);
        const end = new Date(start);
        end.setDate(start.getDate() + 4); // Friday
        return end.toISOString().split('T')[0];
    };

    if (loading) {
        return (
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
                <div className="flex justify-between items-center mb-8">
                    <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <CardSkeleton />
                    <CardSkeleton />
                    <CardSkeleton />
                    <CardSkeleton />
                </div>
                <CardSkeleton />
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
                <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
                    Error: {error}
                </div>
            </div>
        );
    }

    // Apply filters
    const filteredProjects = projects.filter(project => {
        if (selectedProgramme && project.programme_id !== selectedProgramme) return false;
        if (selectedProject && project.id !== selectedProject) return false;
        return true;
    });

    const filteredTasks = tasks.filter(task => {
        const project = projects.find(p => p.id === task.project_id);
        if (!project) return false;
        if (selectedProgramme && project.programme_id !== selectedProgramme) return false;
        if (selectedProject && project.id !== selectedProject) return false;
        return true;
    });

    const filteredTimeEntries = timeEntries.filter(entry => {
        if (selectedProgramme || selectedProject) {
            const task = tasks.find(t => t.id === entry.task_id);
            if (!task) return false;
            const project = projects.find(p => p.id === task.project_id);
            if (!project) return false;
            if (selectedProgramme && project.programme_id !== selectedProgramme) return false;
            if (selectedProject && project.id !== selectedProject) return false;
        }
        return true;
    }).filter(entry => 
        entry.date >= dateRangeStart && entry.date <= dateRangeEnd
    );

    const projectMetrics = calculateProjectMetrics(filteredProjects, filteredTasks);
    const taskMetrics = calculateTaskMetrics(filteredTasks);
    const resourceMetrics = calculateResourceMetrics(users, filteredTasks);
    const ganttTasks = prepareGanttData(filteredTasks, filteredProjects, users);
    const timeTrackingMetrics = calculateTimeTrackingMetrics(filteredTimeEntries, filteredProjects, users);
    
    // User utilization (for selected week)
    const weekEndDate = getWeekEndDate(selectedWeekStart);
    const userUtilization = calculateUserUtilization(
        filteredTimeEntries,
        weeklyPlans,
        filteredProjects,
        users,
        selectedWeekStart,
        weekEndDate
    );

    // Plan vs actual (for selected week)
    const planVsActual = calculatePlanVsActuals(
        filteredTimeEntries,
        weeklyPlans,
        filteredProjects,
        users,
        selectedWeekStart,
        weekEndDate
    );

    // Find issues
    const overdueTasks = filteredTasks.filter(t => {
        if (!t.due_date || t.status === 'completed') return false;
        return new Date(t.due_date) < new Date();
    });

    const unassignedTasks = filteredTasks.filter(t => !t.assigned_to);

    const projectsWithoutTasks = filteredProjects.filter(p => {
        return !filteredTasks.some(t => t.project_id === p.id);
    });

    const overloadedUsers = resourceMetrics.userWorkload.filter(u => u.taskCount > 5);

    const tabs: Tab[] = [
        { id: 'overview', label: 'Project Overview', icon: BarChart3 },
        { id: 'resources', label: 'Resource Capacity', icon: Users },
        { id: 'gantt', label: 'Gantt Chart', icon: List },
        { id: 'issues', label: 'Issues & Risks', icon: AlertTriangle, badge: overdueTasks.length + unassignedTasks.length },
        { id: 'time-tracking', label: 'Time Tracking', icon: Clock },
        { id: 'plan-vs-actual', label: 'Plan vs Actual', icon: TrendingUp }
    ];

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
            <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Reports & Analytics</h1>
            </div>

            {/* Filters */}
            <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Select
                            label="Programme"
                            id="programme-filter"
                            value={selectedProgramme}
                            onChange={(e) => {
                                setSelectedProgramme(e.target.value);
                                setSelectedProject(''); // Reset project when programme changes
                            }}
                        >
                            <option value="">All Programmes</option>
                            {programmes.map(programme => (
                                <option key={programme.id} value={programme.id}>
                                    {programme.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Select
                            label="Project"
                            id="project-filter"
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                        >
                            <option value="">All Projects</option>
                            {projects
                                .filter(project => !selectedProgramme || project.programme_id === selectedProgramme)
                                .map(project => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                        </Select>
                    </div>
                </div>
                {(selectedProgramme || selectedProject) && (
                    <div className="mt-4 flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
                        {selectedProgramme && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                Programme: {programmes.find(p => p.id === selectedProgramme)?.name}
                            </span>
                        )}
                        {selectedProject && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 dark:bg-secondary-900 dark:text-secondary-200">
                                Project: {projects.find(p => p.id === selectedProject)?.name}
                            </span>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setSelectedProgramme('');
                                setSelectedProject('');
                            }}
                        >
                            Clear all
                        </Button>
                    </div>
                )}
            </div>

            <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
            />

            {activeTab === 'overview' && (
                <OverviewTab
                    projectMetrics={projectMetrics}
                    taskMetrics={taskMetrics}
                />
            )}

            {activeTab === 'resources' && (
                <ResourcesTab
                    resourceMetrics={resourceMetrics}
                    taskMetrics={taskMetrics}
                />
            )}

            {activeTab === 'gantt' && (
                <div>
                    <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Project Timeline</h2>
                    <GanttChart tasks={ganttTasks} />
                </div>
            )}

            {activeTab === 'issues' && (
                <IssuesTab
                    overdueTasks={overdueTasks}
                    unassignedTasks={unassignedTasks}
                    projectsWithoutTasks={projectsWithoutTasks}
                    overloadedUsers={overloadedUsers}
                    projects={filteredProjects}
                    users={users}
                    onRescheduleTask={setRescheduleTask}
                />
            )}

            {activeTab === 'time-tracking' && (
                <TimeTrackingTab
                    timeTrackingMetrics={timeTrackingMetrics}
                    dateRangeStart={dateRangeStart}
                    dateRangeEnd={dateRangeEnd}
                    onStartDateChange={setDateRangeStart}
                    onEndDateChange={setDateRangeEnd}
                    onExportCSV={handleExportCSV}
                />
            )}

            {activeTab === 'plan-vs-actual' && (
                <PlanVsActualTab
                    planVsActual={planVsActual}
                    userUtilization={userUtilization}
                    selectedWeekStart={selectedWeekStart}
                    weekEndDate={weekEndDate}
                    onWeekChange={setSelectedWeekStart}
                />
            )}

            {rescheduleTask && (
                <OverdueTaskModal
                    task={rescheduleTask}
                    daysOverdue={Math.ceil((new Date().getTime() - new Date(rescheduleTask.due_date!).getTime()) / (1000 * 60 * 60 * 24))}
                    onSuccess={handleRescheduleSuccess}
                    onClose={() => setRescheduleTask(null)}
                />
            )}
        </div>
    );
};

export default ReportsPage;

